import { Button, Stack, Typography, useTheme } from "@mui/material";

import { GridApiPro, useGridApiContext } from "@mui/x-data-grid-pro";
import { CaretDownIcon } from "@phosphor-icons/react/dist/csr/CaretDown";
import { CaretUpIcon } from "@phosphor-icons/react/dist/csr/CaretUp";
import { useLayoutEffect, useState } from "react";
import { DownloadStatusIcon } from "..";
import { DownloadBundle, DownloadPart } from "../../graphql/types";
import CurvedArrow from "../../icons/CurvedArrow.svg?react";
import { ActionsMenu } from "../ActionsMenu/ActionsMenu";
import { DownloadSamplesButton } from "../DownloadsComponents/DownloadSamplesButton";

const getColumnWidth = (apiRef: React.RefObject<GridApiPro>, columnName: string, align: "left" | "right" = "left") => {
  const column = apiRef.current.getColumn(columnName);
  if (!column) return 0;

  const columns = apiRef.current.getAllColumns();
  const columnIndex = columns.findIndex((col) => col.field === column.field);
  if (columnIndex === -1) return 0;

  const relevantColumns = align === "left" ? columns.slice(0, columnIndex) : columns.slice(columnIndex + 1);

  const columnWidth = relevantColumns.reduce((acc, col) => acc + col.computedWidth, 0);

  return columnWidth;
};

const DownloadRowsForBundle = ({
  bundle,
  statusColumnWidth,
}: {
  bundle: DownloadBundle;
  statusColumnWidth?: number;
}) => {
  const theme = useTheme();
  const apiRef = useGridApiContext();
  const [rowHeight, setRowHeight] = useState(52);
  const [downloadNameWidth, setDownloadNameWidth] = useState(0);
  const [actionsNameWidth, setActionsNameWidth] = useState(0);
  const [statusNameWidth, setStatusNameWidth] = useState(0);

  useLayoutEffect(() => {
    if (apiRef.current) {
      setRowHeight(apiRef.current.unstable_getRowHeight(bundle.id) || 52);
      setDownloadNameWidth(getColumnWidth(apiRef, "name"));
      setActionsNameWidth(getColumnWidth(apiRef, "action", "right"));
      setStatusNameWidth(getColumnWidth(apiRef, "status", "right"));
    }
  }, [apiRef, bundle.id]);
  return (
    <Stack data-testid="download-rows-for-bundle">
      {bundle?.parts
        ?.filter((part): part is DownloadPart => part != null)
        .map((part) => (
          <Stack
            sx={(theme) => ({
              height: `${rowHeight}px`,
              backgroundColor: theme.palette.surfaceLight30.main,
              width: "100%",
              border: `1px solid ${theme.palette.surfaceLight40.main}`,
              alignItems: "center",
              position: "relative",
            })}
            key={part.id}
            direction="row"
          >
            <div
              style={{
                position: "absolute",
                left: `${downloadNameWidth}px`,
                padding: "14px",
              }}
            >
              <CurvedArrow color={theme.palette.text50.main} width="16px" height="16px" />
              <Typography variant="body.small.regular" sx={{ color: "text50.main", paddingLeft: "4px" }}>
                {part.source_table.name}
              </Typography>
            </div>
            <div
              style={{
                position: "absolute",
                right: `${statusNameWidth}px`,
                padding: "12px",
              }}
            >
              <DownloadStatusIcon
                status={bundle.status}
                dirtyRowCount={part.dirty_row_count}
                dirtyRowStatus={part.dirty_row_status}
                rowCount={part.row_count}
                downloadPartId={part.id}
                finished={bundle.finished}
                newDirtyRowCount={part.new_dirty_row_count}
                newDirtyRowStatus={part.new_dirty_row_status}
                statusColumnWidth={statusColumnWidth}
              />
            </div>
            <div
              style={{
                position: "absolute",
                right: `${actionsNameWidth}px`,
                padding: "12px",
              }}
            >
              <ActionsMenu>
                {({ closeModal }) => (
                  <DownloadSamplesButton
                    flow_source_id={part.legacy_flow_source_id || ""}
                    onClick={closeModal}
                    finishedDate={bundle.finished}
                    downloadStatus={bundle.status}
                  />
                )}
              </ActionsMenu>
            </div>
          </Stack>
        ))}
    </Stack>
  );
};

const ExpandCell = ({ isExpanded, isBundle, status }: { isExpanded: boolean; isBundle: boolean; status: string }) => {
  if (!isBundle || status !== "completed") return null;
  return (
    <Stack sx={{ justifyContent: "center", alignItems: "center", height: "100%" }}>
      <Button variant="text" sx={{ color: "text50.main" }}>
        {isExpanded ? <CaretUpIcon width="20px" height="20px" /> : <CaretDownIcon width="20px" height="20px" />}
      </Button>
    </Stack>
  );
};

export { DownloadRowsForBundle, ExpandCell };
